import { kMatterBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'

/**
 * Removes a member from a matter.
 * Only the matter owner can remove members (403 returned otherwise).
 */
export async function deleteMatterMember(matterId: string, userId: string): Promise<void> {
  const sentryTransactionId = nanoid()

  try {
    const user = getAuth().currentUser
    if (!user) throw new Error('User not authenticated.')
    const token = await user.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      method: 'DELETE',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMatterBasePath}${matterId}/members/${userId}`
    const response = await fetch(apiUrl, requestOptions)

    if (!response.ok) {
      // Parse error detail if available
      let detail: unknown
      try {
        const data = await response.json()
        detail = data.detail
      } catch {
        // Response may not be JSON
      }

      // Skip 403 errors in Sentry — expected when non-owner tries to remove
      if (response.status !== 403) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error(`Fetch error - status: ${response.status}`), {
            extra: {
              requestUrl: apiUrl,
              requestOptions,
              response: JSON.stringify(response),
              detail: JSON.stringify(detail),
              onLine: navigator.onLine,
              cookieEnabled: navigator.cookieEnabled,
            },
          })
        })
      }
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(detail)}`)
    }
  } catch (error) {
    // Skip api errors already captured above
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
